import React, { useEffect, useRef, useState } from "react";
import { Box, Button } from "@mui/material";
import {
    EMAIL_TYPABLE_PATTERN,
    getEmailSpellSuggestion,
} from "../../../../utils/emailSpellCheck";
import { useAppContext } from "../../../../context/AppContext";

const DEBOUNCE_MS = 120;
const SPELL_HINT_COLOR = "#d32f2f";

export interface EmailSpellCheckHintProps {
    /** Current email field value */
    email: string;
    /** When true, do not run the checker or show the hint */
    disabled?: boolean;
    /** Increments on signup email `blur` to run an immediate check (no debounce wait). */
    blurRecheckTick?: number;
    /** Apply suggested email when the user clicks the “Did you mean …?” control. */
    onApplySuggestion: (correctedEmail: string) => void;
}

/**
 * Near–real-time domain typo detection using @zootools/email-spell-checker (short debounce + blur refresh).
 * Renders a clickable “Did you mean …?” control under the field to apply the corrected email.
 */
export const EmailSpellCheckHint: React.FC<EmailSpellCheckHintProps> = ({
    email,
    disabled = false,
    blurRecheckTick = 0,
    onApplySuggestion,
}) => {
    const { getTranslation } = useAppContext();
    const [suggestion, setSuggestion] = useState<string | null>(null);
    const [dismissed, setDismissed] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prevBlurTickRef = useRef(0);

    useEffect(() => {
        setDismissed(false);
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        const trimmed = email.trim();
        if (!trimmed || disabled || !EMAIL_TYPABLE_PATTERN.test(trimmed)) {
            setSuggestion(null);
            return;
        }

        const apply = () => {
            const next = getEmailSpellSuggestion(trimmed);
            setSuggestion(next);
        };

        const blurAdvanced = blurRecheckTick > prevBlurTickRef.current;
        prevBlurTickRef.current = blurRecheckTick;

        if (blurAdvanced) {
            apply();
        }

        timerRef.current = setTimeout(apply, DEBOUNCE_MS);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [email, disabled, blurRecheckTick]);

    if (disabled || dismissed || !suggestion) {
        return null;
    }

    const didYouMean = getTranslation("Txt993", "Did you mean");

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 0.75,
                mt: 0.5,
                maxWidth: "100%",
            }}
        >
            <Button
                type="button"
                variant="text"
                size="small"
                aria-label={`${didYouMean} ${suggestion}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                    onApplySuggestion(suggestion);
                    setDismissed(true);
                    setSuggestion(null);
                }}
                sx={{
                    textTransform: "none",
                    p: 0,
                    minWidth: 0,
                    justifyContent: "flex-start",
                    textAlign: "left",
                    color: SPELL_HINT_COLOR,
                    fontSize: "0.75rem",
                    lineHeight: 1.4,
                    maxWidth: "100%",
                    "&:hover": {
                        color: SPELL_HINT_COLOR,
                        backgroundColor: "rgba(211, 47, 47, 0.08)",
                    },
                }}
            >
                <Box
                    component="span"
                    sx={{
                        display: "inline-flex",
                        alignItems: "baseline",
                        flexWrap: "wrap",
                        columnGap: 0.5,
                        rowGap: 0.25,
                    }}
                >
                    <Box component="span">{didYouMean}</Box>
                    <Box component="strong" sx={{ wordBreak: "break-all", fontWeight: 700 }}>
                        {suggestion}
                    </Box>
                    <Box component="span">?</Box>
                </Box>
            </Button>
            <Button
                type="button"
                variant="text"
                size="small"
                onClick={() => setDismissed(true)}
                sx={{
                    textTransform: "none",
                    minWidth: 0,
                    p: 0,
                    fontSize: "0.75rem",
                    color: SPELL_HINT_COLOR,
                    opacity: 0.85,
                    "&:hover": {
                        color: SPELL_HINT_COLOR,
                        opacity: 1,
                        backgroundColor: "rgba(211, 47, 47, 0.08)",
                    },
                }}
            >
                {getTranslation("Txt992", "Dismiss")}
            </Button>
        </Box>
    );
};
